import { supabase, isSupabaseConfigured } from '../lib/supabase';

/**
 * Account Service
 * Functions to fetch and manage user account data from Supabase
 */

/**
 * Check if Supabase is configured before making requests
 */
function checkSupabaseConfig() {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.', data: [] };
  }
  return null;
}

/**
 * Get user's payment history
 * @param {string} userId - User ID
 * @param {Object} options - Pagination and filter options
 * @param {number} options.page - Page number (1-indexed, default: 1)
 * @param {number} options.pageSize - Number of items per page (default: 10)
 * @param {string} options.status - Filter by status (optional)
 * @param {string} options.provider - Filter by provider (optional)
 * @param {string} options.startDate - Filter by start date (ISO string, optional)
 * @param {string} options.endDate - Filter by end date (ISO string, optional)
 * @returns {Promise<Object>} Object with data array and pagination metadata
 */
/**
 * Mark payments as viewed
 * @param {string} userId - User ID
 * @param {Array<string>} paymentIds - Array of payment IDs to mark as viewed
 * @returns {Promise<Object>} Success status
 */
export async function markPaymentsAsViewed(userId, paymentIds) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  if (!paymentIds || paymentIds.length === 0) {
    return { success: true };
  }

  try {
    const { error } = await supabase
      .from('payments')
      .update({ viewed_at: new Date().toISOString() })
      .eq('user_id', userId)
      .in('id', paymentIds)
      .is('viewed_at', null); // Only update if not already viewed

    if (error) {
      console.error('Error marking payments as viewed:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error marking payments as viewed:', error);
    return { success: false, error: error.message };
  }
}

export async function getPayments(userId, options = {}) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return { ...configCheck, pagination: { page: 1, pageSize: 10, total: 0, totalPages: 0 } };

  const page = options.page || 1;
  const pageSize = options.pageSize || 10;
  const offset = (page - 1) * pageSize;

  console.log('📥 Fetching payments for user:', userId, 'page:', page, 'pageSize:', pageSize);
  
  try {
    // Build base query
    let query = supabase
      .from('payments')
      .select('*', { count: 'exact' })
      .eq('user_id', userId);

    // Apply filters
    if (options.status) {
      query = query.eq('status', options.status);
    }
    if (options.provider) {
      query = query.eq('payment_provider', options.provider);
    }
    if (options.startDate) {
      query = query.gte('created_at', options.startDate);
    }
    if (options.endDate) {
      query = query.lte('created_at', options.endDate);
    }
    // Note: "new" filter is handled client-side for better control
    // We fetch all payments and filter in the component

    // Apply ordering
    query = query.order('created_at', { ascending: false });

    // Apply pagination
    query = query.range(offset, offset + pageSize - 1);

    const { data, error, count } = await query;

    if (error) {
      // Handle case where table doesn't exist yet (migration not run)
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        console.warn('⚠️ Payments table does not exist yet. Run migration 004_payments.sql');
        return { 
          success: true, 
          data: [],
          pagination: { page: 1, pageSize: 10, total: 0, totalPages: 0 }
        };
      }
      console.error('❌ Payments query error:', error);
      throw error;
    }

    const total = count || 0;
    const totalPages = Math.ceil(total / pageSize);

    console.log('✅ Payments fetched:', data?.length || 0, 'items (page', page, 'of', totalPages, ')');
    
    return { 
      success: true, 
      data: data || [],
      pagination: {
        page,
        pageSize,
        total,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
      }
    };
  } catch (error) {
    console.error('❌ Error fetching payments:', error);
    return { 
      success: false, 
      error: error.message, 
      data: [],
      pagination: { page: 1, pageSize: 10, total: 0, totalPages: 0 }
    };
  }
}

/**
 * Get user's notices/notifications
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of notices (user-specific and global)
 */
export async function getNotices(userId) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  console.log('📥 Fetching notices for user:', userId);

  try {
    // Get user-specific notices and global notices
    const { data, error } = await supabase
      .from('notices')
      .select('*')
      .or(`user_id.eq.${userId},is_global.eq.true`)
      .order('created_at', { ascending: false });

    if (error) {
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        console.warn('⚠️ Notices table does not exist yet. Run migration 005_notices.sql');
        return { success: true, data: [] };
      }
      console.error('❌ Notices query error:', error);
      throw error;
    }
    console.log('✅ Notices fetched:', data?.length || 0, 'items');
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Error fetching notices:', error);
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Mark a notice as read
 * @param {string} noticeId - Notice ID
 * @returns {Promise<Object>} Updated notice
 */
export async function markNoticeRead(noticeId) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    const { data, error } = await supabase
      .from('notices')
      .update({ is_read: true })
      .eq('id', noticeId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error marking notice as read:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Mark all user notices as read
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Result
 */
export async function markAllNoticesRead(userId) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    const { error } = await supabase
      .from('notices')
      .update({ is_read: true })
      .or(`user_id.eq.${userId},is_global.eq.true`)
      .eq('is_read', false);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error marking all notices as read:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get user's programs
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of user programs
 */
export async function getUserPrograms(userId) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  console.log('📥 Fetching programs for user:', userId);

  try {
    const { data, error } = await supabase
      .from('user_programs')
      .select('*')
      .eq('user_id', userId)
      .order('start_date', { ascending: false });

    if (error) {
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        console.warn('⚠️ User programs table does not exist yet. Run migration 006_user_programs.sql');
        return { success: true, data: [] };
      }
      console.error('❌ Programs query error:', error);
      throw error;
    }
    console.log('✅ Programs fetched:', data?.length || 0, 'items');
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Error fetching user programs:', error);
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Get user's progress entries
 * @param {string} userId - User ID
 * @param {Object} filters - Optional filters (entryType, startDate, endDate)
 * @returns {Promise<Array>} Array of progress entries
 */
export async function getProgressEntries(userId, filters = {}) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  console.log('📥 Fetching progress entries for user:', userId);

  try {
    let query = supabase
      .from('progress_entries')
      .select('*')
      .eq('user_id', userId);

    if (filters.entryType) {
      query = query.eq('entry_type', filters.entryType);
    }

    if (filters.startDate) {
      query = query.gte('entry_date', filters.startDate);
    }

    if (filters.endDate) {
      query = query.lte('entry_date', filters.endDate);
    }

    query = query.order('entry_date', { ascending: false });

    const { data, error } = await query;

    if (error) {
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        console.warn('⚠️ Progress entries table does not exist yet. Run migration 007_progress_tracking.sql');
        return { success: true, data: [] };
      }
      console.error('❌ Progress entries query error:', error);
      throw error;
    }
    console.log('✅ Progress entries fetched:', data?.length || 0, 'items');
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Error fetching progress entries:', error);
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Add a new progress entry
 * @param {string} userId - User ID
 * @param {Object} entryData - Entry data (entryType, entryDate, data, notes)
 * @returns {Promise<Object>} Created entry
 */
export async function addProgressEntry(userId, entryData) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    const { data, error } = await supabase
      .from('progress_entries')
      .insert({
        user_id: userId,
        entry_type: entryData.entryType,
        entry_date: entryData.entryDate,
        data: entryData.data || {},
        notes: entryData.notes || null,
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error adding progress entry:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update a progress entry
 * @param {string} entryId - Entry ID
 * @param {Object} entryData - Updated entry data
 * @returns {Promise<Object>} Updated entry
 */
export async function updateProgressEntry(entryId, entryData) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    const updateData = {};
    if (entryData.entryType) updateData.entry_type = entryData.entryType;
    if (entryData.entryDate) updateData.entry_date = entryData.entryDate;
    if (entryData.data !== undefined) updateData.data = entryData.data;
    if (entryData.notes !== undefined) updateData.notes = entryData.notes;

    const { data, error } = await supabase
      .from('progress_entries')
      .update(updateData)
      .eq('id', entryId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating progress entry:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete a progress entry
 * @param {string} entryId - Entry ID
 * @returns {Promise<Object>} Result
 */
export async function deleteProgressEntry(entryId) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    const { error } = await supabase
      .from('progress_entries')
      .delete()
      .eq('id', entryId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting progress entry:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update user profile
 * @param {string} userId - User ID
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<Object>} Updated profile
 */
export async function updateProfile(userId, profileData) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    const updateData = {};
    if (profileData.full_name !== undefined) updateData.full_name = profileData.full_name;
    if (profileData.phone !== undefined) updateData.phone = profileData.phone;
    if (profileData.avatar_url !== undefined) updateData.avatar_url = profileData.avatar_url;

    const { data, error } = await supabase
      .from('user_profiles')
      .update(updateData)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating profile:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get account summary (counts for dashboard)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Summary data
 */
export async function getAccountSummary(userId) {
  console.log('📥 Fetching account summary for user:', userId);
  
  try {
    // Fetch all data in parallel
    const [paymentsResult, noticesResult, programsResult] = await Promise.all([
      getPayments(userId),
      getNotices(userId),
      getUserPrograms(userId),
    ]);

    const payments = paymentsResult?.data || [];
    const notices = noticesResult?.data || [];
    const programs = programsResult?.data || [];

    const summary = {
      totalPayments: payments.length,
      recentPayments: payments.slice(0, 5),
      unreadNotices: notices.filter(n => !n.is_read).length,
      recentNotices: notices.slice(0, 5),
      activePrograms: programs.filter(p => p.status === 'active').length,
      allPrograms: programs,
    };

    console.log('✅ Account summary created:', summary);
    return { success: true, data: summary };
  } catch (error) {
    console.error('❌ Error fetching account summary:', error);
    return { success: false, error: error.message };
  }
}

export default {
  getPayments,
  getNotices,
  markNoticeRead,
  markAllNoticesRead,
  getUserPrograms,
  getProgressEntries,
  addProgressEntry,
  updateProgressEntry,
  deleteProgressEntry,
  updateProfile,
  getAccountSummary,
  markPaymentsAsViewed,
};
